import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ChatFilterMenu from './ChatFilterMenu';
import ChatScopeMenu from './ChatScopeMenu';
import { chatColors, chatRadii, chatSendSx, chatSizes } from './chatTheme';

/**
 * The composer bar: the filter button, then one bordered input shell holding
 * the slide-scope dropdown, a borderless field and a circular send button.
 *
 * Both popovers anchor to this bar and only one is ever open. Their MUI
 * backdrop is what closes them on a click anywhere else in the panel.
 */
export default function ({
    onSend,
    scope,
    onScopeChange,
    filter,
    onFilterChange,
    chats = [],
    slides = [],
    // Bumped by Chat when a slide's compose button is pressed; 0 means nothing
    // has asked for the caret, so an ordinary mount doesn't steal focus.
    focusKey = 0
}) {
    const inputRef = useRef();
    const barRef = useRef();
    const [text, setText] = useState('');
    const sendingRef = useRef(false);
    const [sending, setSending] = useState(false);
    const [openMenu, setOpenMenu] = useState(null);
    const closeMenus = () => setOpenMenu(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.scrollTop = inputRef.current.scrollHeight;
        }
    }, [text]);

    useEffect(() => {
        if (focusKey && inputRef.current) {
            inputRef.current.focus();
        }
    }, [focusKey]);

    // Clear only once the send has landed: a chat can be refused (a tag whose
    // slide was deleted mid-draft), and clearing up front destroyed the user's
    // message with nothing to paste back.
    const handleSend = async () => {
        if (!text.trim() || sendingRef.current) {
            return;
        }
        sendingRef.current = true;
        setSending(true);
        closeMenus();
        try {
            if ((await onSend(text)) !== false) {
                setText('');
            }
        } finally {
            sendingRef.current = false;
            setSending(false);
        }
    };

    // Insert at the caret rather than appending, so a newline lands where the
    // user is typing when they've moved back into the middle of a draft.
    const insertNewlineAtCaret = (el) => {
        const start = el.selectionStart ?? text.length;
        const end = el.selectionEnd ?? text.length;
        setText(`${text.slice(0, start)}\n${text.slice(end)}`);
        // The caret has to be restored after React re-renders with the new value.
        requestAnimationFrame(() => {
            if (inputRef.current) {
                inputRef.current.selectionStart = start + 1;
                inputRef.current.selectionEnd = start + 1;
            }
        });
    };

    const handleKeyDown = (e) => {
        if (e.key !== 'Enter') {
            return;
        }
        // Modifier+Enter inserts text in code, even when the input is read-only.
        if (sendingRef.current) {
            e.preventDefault();
            return;
        }
        if (e.shiftKey) {
            // Shift+Enter inserts a newline natively; leave it alone.
            return;
        }
        if (e.altKey || e.metaKey || e.ctrlKey) {
            // Ctrl/Meta+Enter never produce a native newline, and neither does
            // Alt+Enter in Chrome on macOS despite #158 assuming it would, so
            // all three insert one explicitly.
            e.preventDefault();
            insertNewlineAtCaret(e.target);
            return;
        }
        e.preventDefault();
        handleSend();
    };

    const canSend = !!text.trim() && !sending;

    return (
        <Box
            sx={(theme) => ({
                flex: '0 0 auto',
                pt: 1.25,
                px: 2,
                pb: 2,
                backgroundColor: theme.palette.common.white
            })}>
            <Box
                ref={barRef}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <ChatFilterMenu
                    filter={filter}
                    chats={chats}
                    slides={slides}
                    open={openMenu === 'filter'}
                    anchorEl={() => barRef.current}
                    onOpen={() => setOpenMenu('filter')}
                    onClose={closeMenus}
                    onChange={onFilterChange}
                />
                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flex: '1 1 auto',
                        minWidth: 0,
                        border: `1px solid ${chatColors(theme).border}`,
                        borderRadius: chatRadii.input,
                        padding: '5px 5px 5px 6px',
                        '& textarea': { resize: 'none !important' }
                    })}>
                    <ChatScopeMenu
                        disabled={sending}
                        scope={scope}
                        slides={slides}
                        open={openMenu === 'scope'}
                        anchorEl={() => barRef.current}
                        onOpen={() => setOpenMenu('scope')}
                        onClose={closeMenus}
                        onChange={onScopeChange}
                    />
                    <Box
                        sx={(theme) => ({
                            flex: '0 0 auto',
                            width: '1px',
                            height: 22,
                            backgroundColor: chatColors(theme).border
                        })}
                    />
                    <InputBase
                        id='chat-text-area'
                        multiline
                        minRows={1}
                        maxRows={4}
                        fullWidth
                        placeholder='Type here to chat'
                        value={text}
                        // Keep the submitted draft intact until the request settles.
                        readOnly={sending}
                        inputRef={inputRef}
                        onChange={(e) => setText(e.target.value)}
                        onFocus={closeMenus}
                        onKeyDown={handleKeyDown}
                        sx={{
                            flex: '1 1 auto',
                            minWidth: 0,
                            fontSize: 14,
                            color: sending ? 'text.disabled' : 'text.primary',
                            py: 0
                        }}
                    />
                    <IconButton
                        aria-label='Send message'
                        onClick={handleSend}
                        disabled={!canSend}
                        sx={chatSendSx(chatSizes.send)}>
                        <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </Box>
            {sending && (
                <Typography role='status' sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary' }}>
                    Sending…
                </Typography>
            )}
        </Box>
    );
}
